import React from 'react';
import { create } from 'zustand';
import type * as pdfjs from 'pdfjs-dist';
import type { FontMapDto } from '@editar-pdf/api';
import type { FontMatchMap } from '@/fonts/fontMatching';
import type { DocumentFontRegistry } from '@/fonts/documentFonts';
import type { DocumentMeta } from '../rendering/DocumentMeta';
import {
  ANNOTATION_DRAWING_COLORS,
  ANNOTATION_LINE_WIDTHS,
  SHAPE_BORDER_WIDTHS,
  SHAPE_DRAWING_COLORS,
} from '@/constants/constants';
import { DrawingTypes } from '@/types';
import { ShapeTypes } from '@/types/shapes';

export type SelectedElementKind = 'text' | 'shape' | 'image' | 'annotation' | 'formField';

export interface SelectedElementState {
  /** Names the subject in the panel header. */
  kind: SelectedElementKind;
  /** 0-based, as the layers count. Displayed +1. */
  pageNumber: number;
  /**
   * Built by the owning layer and rendered opaquely by PropertiesPanel — the panel never
   * imports the element panels. See the design memo for why this seam was kept.
   */
  panel: React.ReactNode;
}

export interface PageImageTier {
  url: string;
  /** CSS-pixel scale the image was requested for (page-coordinate scale, not multiplied by DPR). */
  cssScale: number;
  /** Actual pdfjs render scale used (cssScale * dpr, possibly reduced by the pixel cap). */
  renderScale: number;
}

export type PageImageTierKey = 'thumb' | 'base' | 'hires';

export interface PageImageEntry {
  /** Navigator-width render. Never evicted. Doubles as a placeholder on freshly mounted pages. */
  thumb: PageImageTier | null;
  /** 1× document scale × DPR. LRU-evicted. Fallback when no hi-res yet. */
  base: PageImageTier | null;
  /** Committed-zoom × DPR (the sharp image at current zoom). LRU-evicted. */
  hires: PageImageTier | null;
  /** Background (no-text) variant. 1× only — used to mask edited text boxes. */
  backgroundUrl: string | null;
  status: 'idle' | 'loading' | 'loaded' | 'error';
}

interface EditorStore {
  /** Parsed document metadata — available as soon as client-side parse completes (before upload). */
  documentMeta: DocumentMeta | null;
  /** Live pdfjs proxy — transient, used by ClientImageProvider for pre-upload rendering. Cleared after upload. */
  pdfProxy: pdfjs.PDFDocumentProxy | null;

  setDocumentMeta: (meta: DocumentMeta, pdf: pdfjs.PDFDocumentProxy) => void;
  clearPdfProxy: () => void;
  /**
   * Revokes all cached blob URLs and drops the editor's heavy per-mount resources
   * (page images, pdfjs proxy, document meta), and returns the tool selection to
   * the Text default. Call before loading a new document or on editor unmount.
   *
   * Deliberately does NOT reset the font state — that is document-scoped, not
   * mount-scoped, and is reset by `beginDocumentFonts` when a different document
   * enters. See that method for why.
   */
  clearEditor: () => void;

  /** Server-provided known-font list (GET /fonts). Document-independent — survives clearEditor. */
  knownFonts: FontMapDto[] | null;
  /**
   * Embedded→known font map for the current document (GET /fonts/matches/{fileId}).
   * Keys are the PDF's PostScript font names (subset prefix included); a null
   * value means the server saw the font but has no catalog match (such items
   * may keep rendering with their document-embedded face).
   */
  fontMatches: FontMatchMap | null;
  /** Failure of either fonts endpoint. Fatal for the editor — surfaces the ApplicationError page. */
  fontsError: unknown | null;

  setKnownFonts: (fonts: FontMapDto[]) => void;
  setFontMatches: (matches: FontMatchMap) => void;
  setFontsError: (error: unknown) => void;

  /**
   * Embedded faces of the current document, keyed by pdf.js internal font name
   * (which doubles as the face's registered FontFace family). Populated
   * incrementally as pages parse; matchedFontId refreshed when server matches
   * arrive. The registry decides whether an item's fontName is a document font
   * (render embedded → matched → fallback) or a catalog id.
   */
  documentFonts: DocumentFontRegistry;
  /** Merges newly parsed faces into the registry; existing entries win. */
  addDocumentFonts: (fonts: DocumentFontRegistry) => void;
  /** Wholesale replace — used to refresh matched tiers when matches arrive. */
  setDocumentFonts: (fonts: DocumentFontRegistry) => void;

  /**
   * The document the current font state describes. Identity only — never read
   * as a File, just compared against the editor's current document.
   */
  documentFontsKey: File | null;
  /**
   * Resets the document-scoped font state (registry, matches, error) when — and
   * only when — `key` is a different document than the state was built for.
   *
   * Font state cannot be torn down on editor unmount, because the app-context
   * text items it describes are deliberately retained across the
   * editor → results → "Back" round-trip (see PdfEditor's unmount effect). An
   * unmount-scoped reset left those items pointing at registry entries that no
   * longer existed, and re-armed REMAP_TEXT_ITEM_FONTS to run against an empty
   * map — which rewrites every embedded item to the fallback family. Keying the
   * reset on document identity ties font state to the same lifecycle as the
   * text it describes, while still clearing on a genuine document swap (whose
   * internal font names — `g_d0_f7` — would otherwise collide across documents).
   */
  beginDocumentFonts: (key: File) => void;

  /**
   * The font a text block is showing purely because the user is hovering that font's row
   * in the picker, keyed by block id. Null when nothing is being previewed.
   *
   * Lives here rather than on the text item because a preview is not a choice: routing it
   * through the document context would mark the block edited, reset its rendering mode,
   * push an entry onto the undo stack and log a font change per row the pointer crossed —
   * all for a font the user may never pick. The block renders this name in place of its
   * own committed `fontName` and nothing else changes; picking a font dispatches for real.
   */
  fontPreview: { itemId: string; fontName: string } | null;
  setFontPreview: (preview: { itemId: string; fontName: string } | null) => void;

  /** Code of the currently active editing tool (e.g. 'TEXT', 'DRAW', 'SHAPES'). */
  activeTool: string; // TODO: type?
  /**
   * Erase (whiteout) tool: when true the fill color is sampled from the page under
   * the cursor; when false the user-picked `whiteoutColor` is used instead.
   */
  whiteoutAutoPickColor: boolean;
  /** Hex color used by the erase tool when auto-pick is disabled. */
  whiteoutColor: string;
  /** Active annotation drawing tool (Highlight / Draw) selected in the Draw picker. */
  annotationTool: DrawingTypes;
  /** Hex color used for new annotation strokes. */
  annotationColor: string;
  /** Per-tool stroke width (px). Each tool remembers its own width. */
  annotationLineWidths: Record<DrawingTypes, number>;
  /** Active shape selected in the Shapes picker (Ellipse / Rectangle / Arrow / Line). */
  shapeType: ShapeTypes;
  /** Hex color used for new shapes (border for outlines, stroke for lines/arrows). */
  shapeColor: string;
  /** Per-shape border/line width (px). Each shape remembers its own width. */
  shapeBorderWidths: Record<ShapeTypes, number>;
  /**
   * Bumped on every tool activation, including repeated clicks on the tool that is already
   * active. Monotonic on purpose: the layers key their deselect effects on it, and a counter
   * that restarted per tool produced no change when one first-activated tool replaced another
   * (Text at 1 -> Sign at 1), which left the previous tool's element selected and its panel
   * holding the properties panel.
   */
  toolActivationCount: number;
  /** 1-based index of the page currently visible in the viewport. */
  activePage: number;
  /**
   * The contextual toolbar published by whichever layer element is currently
   * selected. Null when nothing is selected.
   */
  selectedElement: SelectedElementState | null;
  /** True while a pinch or wheel zoom gesture is in progress. */
  isZooming: boolean;
  /**
   * 0-based page indices currently mounted in the main editor viewport (visible + overscan).
   * Written by useVirtualPages; read by ClientImageProvider so LRU eviction never drops a
   * page the user can still see.
   */
  mountedPages: ReadonlySet<number>;
  /** Per-page image cache. Key is the 0-based page index. */
  pageImages: Map<number, PageImageEntry>;
  /**
   * True once the editor is ready to be revealed — i.e. the first page's foreground
   * image has landed (or rendering errored / hung). Driven internally off page 0's
   * render in patchPageImage; the editor keeps the parsing loader up until this flips.
   */
  isEditorReady: boolean;

  /**
   * Activates a tool. Pass `preserveSelection` to hold the activation counter still, which
   * keeps the layers from deselecting: it is for the one flow that switches tools in order
   * to select something (dropping a placed image, then falling back to Select).
   */
  setActiveTool: (tool: string, options?: { preserveSelection?: boolean }) => void;
  setWhiteoutAutoPickColor: (auto: boolean) => void;
  setWhiteoutColor: (color: string) => void;
  setAnnotationTool: (tool: DrawingTypes) => void;
  setAnnotationColor: (color: string) => void;
  /** Sets the stroke width for the currently active annotation tool. */
  setAnnotationLineWidth: (width: number) => void;
  setShapeType: (type: ShapeTypes) => void;
  setShapeColor: (color: string) => void;
  /** Sets the border/line width for the currently active shape. */
  setShapeBorderWidth: (width: number) => void;
  setActivePage: (page: number) => void;
  setSelectedElement: (state: SelectedElementState | null) => void;
  setIsZooming: (zooming: boolean) => void;
  setMountedPages: (pages: ReadonlySet<number>) => void;
  /** Merge a partial entry into the page's cache slot. Pass only the fields you want to overwrite. */
  patchPageImage: (pageIndex: number, patch: Partial<PageImageEntry>) => void;
  clearPageImages: () => void;

  /**
   * Scrolls the editor viewport to the given 1-based page number.
   * Starts as a no-op; PdfEditor registers the real implementation on mount
   * via registerScrollToPage once it has access to the page element refs.
   */
  scrollToPage: (page: number) => void;
  /** Called once by PdfEditor on mount to register the scroll implementation. */
  registerScrollToPage: (fn: (page: number) => void) => void;

  /**
   * The editor's scroll viewport (`.editorContent`). Layers scope their click-outside
   * deselection to it, so that clicking the toolbar, the navigator or the properties panel
   * leaves the selection alone — only the document area drops it.
   */
  viewportEl: HTMLElement | null;
  registerViewport: (el: HTMLElement | null) => void;
}

const revokeEntry = (entry: PageImageEntry) => {
  if (entry.thumb) URL.revokeObjectURL(entry.thumb.url);
  if (entry.base) URL.revokeObjectURL(entry.base.url);
  if (entry.hires) URL.revokeObjectURL(entry.hires.url);
  if (entry.backgroundUrl) URL.revokeObjectURL(entry.backgroundUrl);
};

// Delay between the first page's image landing and revealing the editor — lets the
// <img> paint so the reveal shows content rather than a one-frame blank.
const REVEAL_DELAY_MS = 50;
// Safety net: reveal the editor even if the first page's render hangs without erroring.
const REVEAL_HARD_TIMEOUT_MS = 15_000;

const hasForeground = (entry: PageImageEntry) => !!(entry.hires?.url || entry.base?.url || entry.thumb?.url);

export const useEditorStore = create<EditorStore>((set, get) => {
  let revealDelayTimer: ReturnType<typeof setTimeout> | null = null;
  let revealHardTimer: ReturnType<typeof setTimeout> | null = null;

  const clearRevealTimers = () => {
    if (revealDelayTimer) clearTimeout(revealDelayTimer);
    if (revealHardTimer) clearTimeout(revealHardTimer);
    revealDelayTimer = null;
    revealHardTimer = null;
  };

  const markEditorReady = () => {
    clearRevealTimers();
    if (!get().isEditorReady) set({ isEditorReady: true });
  };

  // Drives the editor reveal off the first page (index 0). Called after each patch to
  // page 0's cache entry: reveals 50ms after the image lands, immediately on render
  // error, and arms a hard-timeout fallback once rendering has started.
  const updateEditorReadiness = (entry: PageImageEntry | undefined) => {
    if (!entry || get().isEditorReady) return;
    if (entry.status === 'error') {
      markEditorReady();
      return;
    }
    if (hasForeground(entry)) {
      if (!revealDelayTimer) revealDelayTimer = setTimeout(markEditorReady, REVEAL_DELAY_MS);
      return;
    }
    if (!revealHardTimer) revealHardTimer = setTimeout(markEditorReady, REVEAL_HARD_TIMEOUT_MS);
  };

  return {
    documentMeta: null,
    pdfProxy: null,

    setDocumentMeta: (meta, pdf) => set({ documentMeta: meta, pdfProxy: pdf }),
    clearPdfProxy: () => set({ pdfProxy: null }),

    knownFonts: null,
    fontMatches: null,
    fontsError: null,

    setKnownFonts: (knownFonts) => set({ knownFonts }),
    setFontMatches: (fontMatches) => set({ fontMatches }),
    setFontsError: (fontsError) => set({ fontsError }),

    documentFonts: {},
    addDocumentFonts: (fonts) => set((s) => ({ documentFonts: { ...fonts, ...s.documentFonts } })),
    setDocumentFonts: (documentFonts) => set({ documentFonts }),

    documentFontsKey: null,
    beginDocumentFonts: (key) => {
      if (get().documentFontsKey === key) return;
      set({
        documentFontsKey: key,
        documentFonts: {},
        fontMatches: null,
        fontsError: null,
      });
    },

    fontPreview: null,
    setFontPreview: (fontPreview) => set({ fontPreview }),

    activeTool: 'TEXT',
    whiteoutAutoPickColor: true,
    whiteoutColor: '#FFFFFF',
    annotationTool: DrawingTypes.Draw,
    annotationColor: ANNOTATION_DRAWING_COLORS[0],
    annotationLineWidths: { ...ANNOTATION_LINE_WIDTHS },
    shapeType: ShapeTypes.Rectangle,
    shapeColor: SHAPE_DRAWING_COLORS[0],
    shapeBorderWidths: { ...SHAPE_BORDER_WIDTHS },
    toolActivationCount: 0,
    activePage: 1,
    selectedElement: null,
    isZooming: false,
    mountedPages: new Set<number>([0]),
    pageImages: new Map(),
    isEditorReady: false,

    setActiveTool: (activeTool, options) =>
      set((s) => ({
        activeTool,
        toolActivationCount: options?.preserveSelection ? s.toolActivationCount : s.toolActivationCount + 1,
      })),
    setWhiteoutAutoPickColor: (whiteoutAutoPickColor) => set({ whiteoutAutoPickColor }),
    setWhiteoutColor: (whiteoutColor) => set({ whiteoutColor }),
    setAnnotationTool: (annotationTool) => set({ annotationTool }),
    setAnnotationColor: (annotationColor) => set({ annotationColor }),
    setAnnotationLineWidth: (width) =>
      set((s) => ({ annotationLineWidths: { ...s.annotationLineWidths, [s.annotationTool]: width } })),
    setShapeType: (shapeType) => set({ shapeType }),
    setShapeColor: (shapeColor) => set({ shapeColor }),
    setShapeBorderWidth: (width) =>
      set((s) => ({ shapeBorderWidths: { ...s.shapeBorderWidths, [s.shapeType]: width } })),
    setActivePage: (activePage) => {
      set({ activePage });
    },
    setSelectedElement: (selectedElement) => set({ selectedElement }),
    setIsZooming: (isZooming) => set({ isZooming }),
    setMountedPages: (mountedPages) => set({ mountedPages }),

    patchPageImage: (key, patch) => {
      set((s) => {
        const next = new Map(s.pageImages);
        const existing = next.get(key) ?? {
          thumb: null,
          base: null,
          hires: null,
          backgroundUrl: null,
          status: 'idle' as const,
        };
        next.set(key, { ...existing, ...patch });
        return { pageImages: next };
      });
      if (key === 0) updateEditorReadiness(get().pageImages.get(0));
    },

    clearPageImages: () => {
      get().pageImages.forEach(revokeEntry);
      set({ pageImages: new Map() });
    },

    clearEditor: () => {
      clearRevealTimers();
      get().pageImages.forEach(revokeEntry);
      set({
        pageImages: new Map(),
        documentMeta: null,
        pdfProxy: null,
        activePage: 1,
        mountedPages: new Set([0]),
        isEditorReady: false,
        fontPreview: null,
        // Entering the editor always starts on the Text tool, whether the user is
        // coming back from the results/download page or opening a new document.
        // Reset here (on the way out) rather than on mount so no consumer ever
        // renders or arms a layer with the previous session's tool.
        activeTool: 'TEXT',
        toolActivationCount: 0,
        // Font state (knownFonts, fontMatches, fontsError, documentFonts) is not
        // touched here: knownFonts is document-independent, and the rest is
        // document-scoped and reset by beginDocumentFonts on a document swap.
      });
    },

    // Replaced on first PdfEditor mount via registerScrollToPage.
    scrollToPage: () => {},
    registerScrollToPage: (fn) => set({ scrollToPage: fn }),

    viewportEl: null,
    registerViewport: (viewportEl) => set({ viewportEl }),
  };
});
